import React, { useContext } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';

import Clickable from '@prisjakt/ui-clickable';
import { Behavior, FontEmphasis } from '@prisjakt/ui/text/variants';
import ExpertAvatar from '../expert-avatar';

import { ExpertUserCardSize } from 'src/areas/expert/types';

import * as S from './styles';

type Props = {
  className?: string;
  experts: Expert[];
  size?: ExpertUserCardSize;
  subtitle?: string | React.ReactNode;
};

type Expert = {
  avatar: string;
  name: string;
  url?: string;
};

export const ExpertUserCard = (props: Props) => {
  const {
    className,
    experts,
    size = ExpertUserCardSize.SMALL,
    subtitle,
  } = props;
  const { t, renderMode, linkTo } = useContext(AppContext);

  const avatarSize =
    size === ExpertUserCardSize.MEDIUM
      ? S.USER_AVATAR_SIZE_MEDIUM
      : S.USER_AVATAR_SIZE_SMALL;

  const container = experts.length === 1 ? Clickable : 'div';
  const containerProps =
    experts.length === 1
      ? {
          to: linkTo(
            'expertProfile',
            { semantic_id: experts[0].url },
            renderMode === 'native' ? { view: 'native' } : undefined
          ),
          isExternal: renderMode === 'native',
        }
      : undefined;

  return (
    <S.Container
      className={className}
      as={container}
      $size={avatarSize}
      {...containerProps}
    >
      {experts.map((expert, index) => (
        <S.Expert
          key={expert.name}
          $index={experts.length - index}
          $overlap={index}
          $size={avatarSize}
        >
          <S.Avatar>
            <ExpertAvatar
              avatar={expert.avatar}
              displayName={expert.name}
              size={avatarSize}
            />
          </S.Avatar>

          {experts.length === 1 && (
            <>
              {size === ExpertUserCardSize.MEDIUM ? (
                <S.NameMedium
                  behavior={Behavior.Oneline}
                  $showSubtitle={Boolean(subtitle)}
                >
                  {expert.name}
                </S.NameMedium>
              ) : (
                <S.NameSmall
                  behavior={Behavior.Oneline}
                  $showSubtitle={Boolean(subtitle)}
                >
                  {expert.name}
                </S.NameSmall>
              )}

              {subtitle && (
                <S.Subtitle emphasis={FontEmphasis.MEDIUM} $size={size}>
                  {subtitle}
                </S.Subtitle>
              )}
            </>
          )}

          {experts.length > 1 && index === experts.length - 1 && (
            <>
              {size === ExpertUserCardSize.MEDIUM ? (
                <S.NameMedium $showSubtitle={false} behavior={Behavior.Oneline}>
                  {t('expert', 'guide.lists-expert-plural', {
                    count: experts.length,
                  })}
                </S.NameMedium>
              ) : (
                <S.NameSmall $showSubtitle={false} behavior={Behavior.Oneline}>
                  {t('expert', 'guide.lists-expert-plural', {
                    count: experts.length,
                  })}
                </S.NameSmall>
              )}
            </>
          )}
        </S.Expert>
      ))}
    </S.Container>
  );
};
